/**
 * questionsData.js
 *
 * Question-format URLs targeting long-tail conversational queries + Google's
 * AI Overviews / "People Also Ask" boxes. Each question gets its own URL
 * (/questions/{slug}) with a direct-answer summary, deeper editorial body,
 * relevant HBH video, and a match-me CTA at the bottom.
 *
 * Curated for commercial-intent queries around the 3 growth surfaces:
 *   - House and land packages
 *   - Choosing / searching for builders
 *   - Building a new home (process, timeline, decisions)
 *
 * Fields:
 *   slug             : URL-safe stable slug
 *   question         : natural-language question (used as H1 and title)
 *   shortAnswer      : 40-60 word direct answer for the featured-snippet /
 *                      AI Overview eligibility. First sentence must directly
 *                      answer the question.
 *   longAnswer       : longer editorial body (2-6 paragraphs) rendered after
 *                      the short answer. Optional.
 *   category         : one of: packages | builders | land | contracts |
 *                      finance | first-home | process
 *   hbhEpisodeNumber : preferred HBH episode to embed on the page
 *   relatedTo        : array of internal BuildPilot deep-links to surface
 *                      near the answer (topical siloing)
 *   updatedAt        : ISO date for structured data
 */

export const QUESTIONS = [
  {
    slug: 'what-is-a-house-and-land-package',
    question: 'What is a house and land package?',
    category: 'packages',
    hbhEpisodeNumber: 7,
    updatedAt: '2026-05-15',
    shortAnswer:
      'A house and land package bundles a specific block of land with a specific builder\'s home design into a single contract structure, priced together. You lock in the land price and the build price at the same time, then contract with the builder for the build and settle on the land separately. Common in Adelaide growth corridors, quicker to start than buying land and choosing a builder separately.',
    longAnswer: [
      'House and land packages are marketed jointly by a builder (or a builder-developer partnership) but sit on two contracts: a land contract (with the developer) and a building contract (with the builder). You pay a deposit to hold the land, a deposit to the builder, then progress payments during the build.',
      'They\'re popular because they remove one big decision (which builder), give a fixed inclusions list, and often carry promotional site-cost caps. The trade-off: you\'re locked to the builder\'s designs and the estate\'s design guidelines, and the land you see today may not be titled yet.',
      'BuildPilot\'s Adelaide house and land packages are all from partner builders, filterable by suburb and build type. No listing fees are paid by the developer or builder to appear.',
    ],
    relatedTo: [
      { label: 'Browse Adelaide House & Land packages', to: '/house-and-land-adelaide' },
      { label: 'Should you buy land or a package first?', to: '/questions/should-i-buy-land-or-a-package-first' },
      { label: 'Choosing land guide', to: '/choosing-land' },
    ],
  },
  {
    slug: 'should-i-buy-land-or-a-package-first',
    question: 'Should I buy land first or a house and land package?',
    category: 'packages',
    hbhEpisodeNumber: 6,
    updatedAt: '2026-05-15',
    shortAnswer:
      'Buy a house and land package if you want speed, fixed inclusions and a builder-managed handoff. Buy bare land first if you want maximum flexibility on builder choice, custom design, or if you already know the builder you want to use. Package pricing is often cheaper due to developer + builder rebates; bare-land plus custom-tender is usually slower but more tailored.',
    longAnswer: [
      'Packages sync your land settlement and build start. You know the total from day one. The downside: you can only pick from the builder\'s pre-designed range and their nominated estates.',
      'Bare land gives you the flexibility to interview any Adelaide builder, tender your build separately, and pick the exact block that suits your family and orientation preference. It requires two finance approvals and two contract negotiations.',
      'Rule of thumb: first home buyers with a tight timeline and simple needs suit packages. Experienced buyers, KDR buyers, or anyone with a strong design preference are better off buying land first.',
    ],
    relatedTo: [
      { label: 'House & Land packages in Adelaide', to: '/house-and-land-adelaide' },
      { label: 'Land for sale in Adelaide', to: '/land-for-sale-adelaide' },
      { label: 'Choosing land guide', to: '/choosing-land' },
    ],
  },
  {
    slug: 'how-do-i-choose-a-home-builder-in-adelaide',
    question: 'How do I choose a home builder in Adelaide?',
    category: 'builders',
    hbhEpisodeNumber: 105,
    updatedAt: '2026-05-15',
    shortAnswer:
      'Match on four things in this order: (1) build type they specialise in, (2) service area they build in, (3) price band that fits your budget, and (4) design language you like. Then check they\'ve completed builds like yours in the last 12 months, ask for a fixed-price quote (not an estimate), and verify their SA builder licence with CBS.',
    longAnswer: [
      'Volume builders (Metricon, Simonds, Fairmont, Rossdale) sit in a different price band and design category than project builders (Australian Building Co, Lofty, Alete) and those sit differently again to custom builders (Format, Orizon, Buildscope). Pick the tier before you pick the builder.',
      'Every Adelaide builder should have: (a) an active SA builder licence, (b) HIA or MBA membership, (c) home indemnity insurance for the build value, (d) recent completed builds you can view or drive past. Ask for a fixed-price quote with itemised inclusions, not a "from" price.',
      'BuildPilot\'s Find a Builder questionnaire shortlists partner builders who match your budget, block and build type in a 3-minute questionnaire.',
    ],
    relatedTo: [
      { label: 'Compare Adelaide partner builders', to: '/builders' },
      { label: 'Get matched with builders', to: '/find-a-builder' },
      { label: 'What "trusted builder" should mean', to: '/home-builders-adelaide' },
    ],
  },
  {
    slug: 'how-long-does-it-take-to-build-a-house-in-adelaide',
    question: 'How long does it take to build a house in Adelaide?',
    category: 'process',
    hbhEpisodeNumber: 127,
    updatedAt: '2026-05-15',
    shortAnswer:
      'Most Adelaide new home builds take 8-14 months from site start to handover. Volume builder single-storey homes are typically 8-10 months; double-storey adds 2-3 months. Custom builds run 12-18 months. Add another 3-9 months for pre-site: finance, design, contract, developer approval and utilities. Weather delays on ground-floor works add 2-4 weeks.',
    longAnswer: [
      'The "how long" question hides two clocks: pre-site (from signed contract to first slab pour) and construction (slab to handover). Pre-site is where most timelines slip - developer approvals, utility connections, soil test rework and design revisions all sit here.',
      'Construction itself is roughly: slab (2-3 weeks), frame (3-6 weeks), lock-up (4-6 weeks), fixing / plaster (5-8 weeks), fit-off / practical completion (4-8 weeks). The published "build time" in most contracts covers construction, not pre-site.',
      'To keep the whole project moving, most Adelaide partner builders bring pre-site under their own management once contract is signed. Ask what "days" their contract counts and whether inclement weather stops the clock.',
    ],
    relatedTo: [
      { label: 'Build timeline guide', to: '/building-a-new-home/build-timeline' },
      { label: 'Building glossary', to: '/terminology' },
    ],
  },
  {
    slug: 'what-are-site-costs-when-building-a-home',
    question: 'What are site costs when building a home?',
    category: 'process',
    hbhEpisodeNumber: 16,
    updatedAt: '2026-05-15',
    shortAnswer:
      'Site costs are the additional expenses to prepare your block for building - separate from the base house price. They cover the soil test, engineering, cut-and-fill, retaining walls, connections, and any regulatory overlays that apply to your block. Range: $15,000-$60,000+ depending on soil class, slope, retaining and how much fall is on the block.',
    longAnswer: [
      'Most Adelaide builders quote a "site cost allowance" upfront, then finalise after the soil test and contour survey. A P-class soil (problem soil) can multiply your slab cost by 2-3x compared to A-class (rock). Sloping blocks need retaining, split slabs or lifted floor systems.',
      'Beyond soil, watch for: sewer connection depth, stormwater tanks or infiltration, driveway crossovers, and any overlays (bushfire, acoustic, flood, heritage) which each add compliance cost.',
      'When comparing builder quotes, normalise the site cost allowance - two identical base prices with $20K vs $45K allowances is a $25K real-world difference.',
    ],
    relatedTo: [
      { label: 'Choosing land in Adelaide', to: '/choosing-land' },
      { label: 'Building glossary: site costs', to: '/terminology' },
    ],
  },
  {
    slug: 'what-is-a-progress-payment-in-a-building-contract',
    question: 'What is a progress payment in a building contract?',
    category: 'contracts',
    hbhEpisodeNumber: 117,
    updatedAt: '2026-05-15',
    shortAnswer:
      'A progress payment is a scheduled instalment paid to your builder as each construction milestone is completed. Standard South Australian residential building contracts use five stages: deposit (5%), base (15-20%), frame (20-25%), lock-up (20-25%), fixing (20%), and practical completion (5-10%). Each stage must be independently inspected before payment is released.',
    longAnswer: [
      'Progress payments protect both parties: the builder gets paid as work is done, and you only pay for work actually completed. Never pay ahead of the schedule.',
      'Practical steps: (1) inspect the work yourself before signing off; (2) don\'t rely on the builder-appointed surveyor - consider an independent inspector; (3) release payment via your bank only after verifying the stage claim.',
      'If the builder is asking for progress payments beyond the schedule or for extras not documented as variations, stop and get contract review advice before paying.',
    ],
    relatedTo: [
      { label: 'Building glossary', to: '/terminology' },
      { label: 'One on One contract review', to: '/one-on-one-support' },
    ],
  },
  {
    slug: 'what-does-a-building-inspector-actually-check',
    question: 'What does a building inspector actually check?',
    category: 'process',
    hbhEpisodeNumber: 109,
    updatedAt: '2026-05-15',
    shortAnswer:
      'A private building inspector checks that construction complies with the approved plans, the National Construction Code (NCC), Australian Standards, and any state-specific rules (Development Act in SA). Typical inspection points: pre-slab (soil, footings, formwork), frame (structural), pre-plaster (waterproofing, insulation, services rough-in), and final (defects list). Cost: $600-$1,200 per inspection in Adelaide.',
    longAnswer: [
      'Your builder-nominated surveyor is required to sign off at certain stages, but they\'re paid by the builder. Appointing an independent private inspector adds a second set of eyes on quality and gives you leverage on defect claims.',
      'The pre-handover inspection is the most important. Once you sign the handover certificate, you shift from a build-defects regime to a warranty-defects regime, which is more restrictive. Never skip the pre-handover walk-through.',
    ],
    relatedTo: [
      { label: 'Building inspectors guide', to: '/building-inspectors' },
      { label: 'Building glossary', to: '/terminology' },
    ],
  },
  {
    slug: 'what-deposit-do-i-need-to-build-a-house',
    question: 'What deposit do I need to build a house in Adelaide?',
    category: 'finance',
    hbhEpisodeNumber: 118,
    updatedAt: '2026-05-15',
    shortAnswer:
      'For a house and land package you typically need 5-10% of the land price to hold the land, plus a 5% builder deposit at contract signing. Total upfront cash is usually 10-20% of the combined package price. First home buyers using government schemes can get in with as little as 5% via the Home Guarantee Scheme; standard buyers with LMI need 5-10%; borrowing without LMI needs 20%.',
    longAnswer: [
      'The land deposit is paid to the developer\'s conveyancer at contract signing. Your builder deposit sits in trust until the first construction stage begins. Both are refundable in specific contract-cooling-off conditions.',
      'Ask a broker to model your actual cash requirement including stamp duty concessions (SA offers reductions for first home buyers on new builds), LMI premiums, and interest during construction. Cash needed at signing is usually well below the maximum "20%" figure.',
    ],
    relatedTo: [
      { label: 'Building a new home', to: '/building-a-new-home' },
      { label: 'Adelaide House & Land packages', to: '/house-and-land-adelaide' },
    ],
  },
  {
    slug: 'can-i-change-a-standard-floorplan',
    question: 'Can I change a standard floorplan when building?',
    category: 'process',
    hbhEpisodeNumber: 120,
    updatedAt: '2026-05-15',
    shortAnswer:
      'Yes but with limits. Non-structural changes (moving an internal wall, extending an alfresco, swapping tiles) are usually straightforward and quoted as variations. Structural changes (moving load-bearing walls, altering roof lines, changing exterior footprint) trigger re-engineering, re-drafting and often re-pricing - and some volume builders won\'t allow them. Ask early which changes are in scope before signing.',
    relatedTo: [
      { label: 'Home design guidance', to: '/home-design' },
      { label: 'Compare builders (design flexibility varies)', to: '/builders' },
    ],
  },
  {
    slug: 'what-is-a-provisional-sum-in-a-building-contract',
    question: 'What is a provisional sum in a building contract?',
    category: 'contracts',
    hbhEpisodeNumber: 117,
    updatedAt: '2026-05-15',
    shortAnswer:
      'A provisional sum is an estimated amount for a specific item in your contract where the exact final cost isn\'t known at signing - commonly used for site costs, landscaping, or council fees. You\'re invoiced for the actual cost at the end, so provisional sums can go up OR down. Watch for provisional sums that hide items which should be fixed.',
    longAnswer: [
      'Contracts also use "prime cost" items - a specified budget for a selection like a bathtub or oven. Difference: PC items are for items you choose from a supplier catalogue; provisional sums are for site-specific work whose extent is unknown.',
      'Red flag: a builder using provisional sums for things they should be able to fix-price (like a driveway of known width, or standard connections). Always ask "why is this a PS not fixed?" and get the range they expect the actual to land in.',
    ],
    relatedTo: [
      { label: 'Building glossary', to: '/terminology' },
      { label: 'One on One contract review', to: '/one-on-one-support' },
    ],
  },
  {
    slug: 'what-is-developer-approval-when-building',
    question: 'What is developer approval when building on new land?',
    category: 'process',
    hbhEpisodeNumber: 108,
    updatedAt: '2026-05-15',
    shortAnswer:
      'Developer approval is the sign-off you need from the estate developer (separate from your council approval) before you can build. The developer checks your design against the estate\'s design guidelines - things like facade materials, roof pitch, driveway placement, fencing, landscaping. It usually takes 2-6 weeks and can require design changes if the guidelines are strict.',
    longAnswer: [
      'Most Adelaide growth-corridor estates (Lightsview, Blakes Crossing, Munno Para, etc.) enforce guidelines to protect the estate look. Some are light-touch, some are quite prescriptive.',
      'Ask for the estate\'s design guidelines BEFORE you sign your building contract and choose your design. A design that clears developer approval on one estate may need changes on another.',
    ],
    relatedTo: [
      { label: 'Choosing land guide', to: '/choosing-land' },
      { label: 'Adelaide suburb guides', to: '/suburbs' },
    ],
  },
  {
    slug: 'what-is-a-turnkey-house-and-land-package',
    question: 'What is a turnkey house and land package?',
    category: 'packages',
    hbhEpisodeNumber: 28,
    updatedAt: '2026-05-15',
    shortAnswer:
      'A "turnkey" package means everything is included so you can literally walk in and turn the key on the front door - no extra costs for driveways, fencing, landscaping, blinds, letterbox, clothesline etc. Compare to "base" or "fixed price" builds which typically only include the home\'s structure and standard inclusions. Turnkey often costs 10-15% more than base but eliminates the biggest hidden-cost surprises.',
    longAnswer: [
      'Not all "turnkey" packages are equal. Look for the inclusions list explicitly listing: driveway, fencing (full perimeter), landscaping (front + rear), window furnishings, letterbox, clothesline, tv antenna, pest control certificate.',
      'Turnkey suits investors and first home buyers on a fixed budget who can\'t easily fund extras. Owner-occupiers who want a specific driveway finish, fence style or landscape design may prefer a base build.',
    ],
    relatedTo: [
      { label: 'Adelaide House & Land packages', to: '/house-and-land-adelaide' },
      { label: 'Get matched with a builder', to: '/find-a-builder' },
    ],
  },
  {
    slug: 'when-should-i-lock-in-my-build-price',
    question: 'When should I lock in my build price?',
    category: 'contracts',
    hbhEpisodeNumber: 103,
    updatedAt: '2026-05-15',
    shortAnswer:
      'Lock the build price in your building contract before signing - that\'s your fixed price commitment. Base prices from builder marketing are typically only "protected" (i.e. held) for 90-180 days after tender. Any delay past that window can trigger a price increase clause. Ask your builder for their tender-to-contract cooling window in writing.',
    relatedTo: [
      { label: 'One on One contract review', to: '/one-on-one-support' },
      { label: 'Building glossary', to: '/terminology' },
    ],
  },
  {
    slug: 'what-is-a-fixed-price-building-contract',
    question: 'What is a fixed price building contract?',
    category: 'contracts',
    hbhEpisodeNumber: 117,
    updatedAt: '2026-05-15',
    shortAnswer:
      'A fixed price building contract sets the total build price when you sign, subject only to specific written variations. It shifts material and labour risk from you to the builder. In SA, the HIA Fixed Price Contract is the industry standard for residential new builds. Beware "fixed-price" contracts that carry heavy provisional sums or open-ended cost-escalation clauses - those aren\'t truly fixed.',
    relatedTo: [
      { label: 'One on One contract review', to: '/one-on-one-support' },
      { label: 'Building glossary', to: '/terminology' },
    ],
  },
  {
    slug: 'what-should-i-know-about-titled-vs-untitled-land',
    question: 'What is titled vs untitled land?',
    category: 'land',
    hbhEpisodeNumber: 29,
    updatedAt: '2026-05-15',
    shortAnswer:
      'Titled land is registered with the SA Lands Titles Office and ready to build on now. Untitled (or unregistered) land is land the developer is selling before formal subdivision registration - typically 6-24 months away from settlement. Untitled land is often cheaper and locks in a growth-corridor position, but you can\'t start your build until title issues, and construction lending is harder to arrange.',
    relatedTo: [
      { label: 'Land for sale in Adelaide', to: '/land-for-sale-adelaide' },
      { label: 'Adelaide House & Land packages', to: '/house-and-land-adelaide' },
    ],
  },
  {
    slug: 'how-much-does-it-cost-to-connect-utilities',
    question: 'How much does it cost to connect utilities to a new home?',
    category: 'process',
    hbhEpisodeNumber: 16,
    updatedAt: '2026-05-15',
    shortAnswer:
      'In Adelaide, connecting utilities to a new home typically costs $6,000-$18,000 including electricity ($1,500-$4,000), gas or all-electric ($500-$3,500), water ($3,000-$7,000), stormwater and sewer ($1,500-$5,000), and NBN ($300-$2,500). Costs vary by block, depth to mains, and whether the estate is fully serviced. Many builders include connections in their site cost allowance; others quote them as a provisional sum.',
    relatedTo: [
      { label: 'Choosing land guide', to: '/choosing-land' },
      { label: 'Building glossary', to: '/terminology' },
    ],
  },
  {
    slug: 'what-is-the-difference-between-base-price-and-turnkey',
    question: 'What is the difference between base price and turnkey?',
    category: 'packages',
    hbhEpisodeNumber: 10,
    updatedAt: '2026-05-15',
    shortAnswer:
      'Base price is the builder\'s advertised starting price for a specific design with their standard inclusions - it usually excludes site costs, upgrades, driveway, fencing, landscaping and window furnishings. Turnkey means everything is included and the home is ready to live in on handover. Between these two: "fixed-price base" adds site costs to the base but still excludes external works.',
    relatedTo: [
      { label: 'Compare Adelaide partner builders', to: '/builders' },
      { label: 'Get matched with builders', to: '/find-a-builder' },
    ],
  },
  {
    slug: 'how-do-i-negotiate-with-a-home-builder',
    question: 'How do I negotiate with a home builder?',
    category: 'builders',
    hbhEpisodeNumber: 105,
    updatedAt: '2026-05-15',
    shortAnswer:
      'Come prepared: know your budget bracket, block details, your must-have design changes, and 2-3 comparable quotes. The strongest negotiating leverage isn\'t price - it\'s trading inclusions (better appliances, higher ceilings, larger tiles) for a fixed price. Volume builders discount at end-of-month, end-of-quarter and during promotional periods. Custom builders rarely discount price but will trade scope.',
    relatedTo: [
      { label: 'Compare Adelaide partner builders', to: '/builders' },
      { label: 'Get matched with builders', to: '/find-a-builder' },
    ],
  },
  {
    slug: 'what-is-a-knockdown-rebuild',
    question: 'What is a knockdown rebuild?',
    category: 'process',
    hbhEpisodeNumber: 122,
    updatedAt: '2026-05-15',
    shortAnswer:
      'A knockdown rebuild (KDR) is when you demolish an existing home and build a new one on the same block. In Adelaide it makes sense when the block is worth keeping (established suburb, mature trees, good orientation) but the existing home is beyond cost-effective renovation. Total cost is typically $500K-$900K including demolition ($15-30K), rebuild, site costs and finance holding costs.',
    relatedTo: [
      { label: 'Knockdown rebuild guide', to: '/knockdown-rebuild' },
      { label: 'Adelaide House & Land packages', to: '/house-and-land-adelaide' },
    ],
  },
  {
    slug: 'do-i-need-a-conveyancer-when-buying-land',
    question: 'Do I need a conveyancer when buying land?',
    category: 'land',
    hbhEpisodeNumber: 101,
    updatedAt: '2026-05-15',
    shortAnswer:
      'Yes. A conveyancer (or property lawyer) reviews the land contract, orders and reviews Section 7 statements, coordinates with your bank, pays stamp duty, and lodges the transfer at Lands Titles. In SA, expect $800-$1,800 for new land conveyancing. For a house and land package, you use two conveyancers: one for the land contract and typically your builder\'s nominated one for the build contract, or a single one for both.',
    relatedTo: [
      { label: 'Choosing land guide', to: '/choosing-land' },
      { label: 'Land for sale in Adelaide', to: '/land-for-sale-adelaide' },
    ],
  },
  {
    slug: 'what-questions-should-i-ask-a-home-builder',
    question: 'What questions should I ask a home builder before signing?',
    category: 'builders',
    hbhEpisodeNumber: 111,
    updatedAt: '2026-05-15',
    shortAnswer:
      'Ask (1) can I see your active SA builder licence and insurance certificates, (2) can I view a recently completed build (not a display home), (3) is this quote fixed or provisional, (4) what\'s your average build time for this design on this block, (5) what\'s the process if we hit a delay or defect, (6) which items are prime cost vs provisional sum, (7) what happens if I need to change something after signing.',
    relatedTo: [
      { label: 'Questions to ask builders', to: '/questions-to-ask-builders' },
      { label: 'Get matched with builders', to: '/find-a-builder' },
    ],
  },
  {
    slug: 'can-i-build-on-a-sloping-block',
    question: 'Can I build on a sloping block?',
    category: 'land',
    hbhEpisodeNumber: 100,
    updatedAt: '2026-05-15',
    shortAnswer:
      'Yes. Sloping blocks in Adelaide are common and buildable, but they cost more to prepare. Options: cut-and-fill for a level slab (cheapest but limits design), split-level slabs (more expensive but keeps character), or lifted floor systems on stumps or waffle-pod (adds cost but suits steep sites). Expect $20K-$80K in extra site costs for a mid-slope block versus a flat one.',
    relatedTo: [
      { label: 'Choosing land guide', to: '/choosing-land' },
      { label: 'Land for sale in Adelaide', to: '/land-for-sale-adelaide' },
    ],
  },
  {
    slug: 'what-warranty-do-new-homes-come-with',
    question: 'What warranty does a new home come with in SA?',
    category: 'process',
    hbhEpisodeNumber: 111,
    updatedAt: '2026-05-15',
    shortAnswer:
      'In South Australia, licensed builders provide statutory home indemnity insurance covering structural defects for 5 years and non-structural defects for 2 years from handover under the Building Work Contractors Act. Many builders offer extended structural warranties (10-25 years) on top. Warranty claims process: notify the builder in writing, allow reasonable time to inspect and rectify, escalate to CBS SA if unresolved.',
    relatedTo: [
      { label: 'Building glossary', to: '/terminology' },
      { label: 'Compare Adelaide partner builders', to: '/builders' },
    ],
  },
  {
    slug: 'what-is-a-soil-test-in-home-building',
    question: 'What is a soil test in home building?',
    category: 'process',
    hbhEpisodeNumber: 110,
    updatedAt: '2026-05-15',
    shortAnswer:
      'A soil test (geotechnical report) analyses the ground on your block to determine slab and footing design. In SA, soil classifications range from A-class (rock, best) through S/M/H/E-class to P-class (problem soil, worst). Your soil class drives your slab cost - moving from A to H can add $15,000-$40,000 to your site cost. Most builders quote assuming M-class and re-quote after the test.',
    relatedTo: [
      { label: 'Choosing land guide', to: '/choosing-land' },
      { label: 'Building glossary', to: '/terminology' },
    ],
  },
  {
    slug: 'what-is-the-first-home-owner-grant-sa',
    question: 'What is the First Home Owner Grant in SA?',
    category: 'first-home',
    hbhEpisodeNumber: 115,
    updatedAt: '2026-05-15',
    shortAnswer:
      'The SA First Home Owner Grant is $15,000 for first home buyers building or buying a new home valued under $650,000. You must live in the home for at least 6 continuous months within 12 months of settlement. It stacks with other schemes including the Federal Home Guarantee Scheme (5% deposit) and SA stamp duty concessions on new builds. Apply through your conveyancer at settlement, not before.',
    relatedTo: [
      { label: 'Building a new home guide', to: '/building-a-new-home' },
      { label: 'Adelaide House & Land packages', to: '/house-and-land-adelaide' },
    ],
  },
];

export const QUESTION_CATEGORIES = {
  packages: 'House & Land Packages',
  builders: 'Choosing a Builder',
  land: 'Buying Land',
  contracts: 'Contracts',
  finance: 'Finance & Grants',
  'first-home': 'First Home Buyers',
  process: 'The Building Process',
};

const _bySlug = Object.fromEntries(QUESTIONS.map((q) => [q.slug, q]));
export const getQuestionBySlug = (slug) => _bySlug[slug] || null;
export const getQuestionsByCategory = (cat) =>
  QUESTIONS.filter((q) => q.category === cat);
export const getRelatedQuestions = (currentSlug, cat, limit = 4) => {
  return QUESTIONS.filter(
    (q) => q.slug !== currentSlug && q.category === cat
  ).slice(0, limit);
};
